import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Toast } from 'antd-mobile';
import { ClockCircleOutline } from 'antd-mobile-icons';
import dayjs from 'dayjs';
import SafeArea from '@/components/SafeArea';
import EmptyState from '@/components/EmptyState';
import { HouseGlyph } from '@/components/Icon';
import { search, SearchResult } from '@/api/search';
import './styles.css';

const SEARCH_HISTORY_KEY = 'wine_search_history';

type SearchTab = 'all' | 'customers' | 'villages' | 'records';

function SearchGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="10.8" cy="10.8" r="7.2" />
      <path d="m16.2 16.2 4.6 4.6" />
    </svg>
  );
}

function Chevron() {
  return (
    <svg className="search-pro-chevron" viewBox="0 0 12 20" aria-hidden="true">
      <path d="m2.5 2.5 7 7.5-7 7.5" />
    </svg>
  );
}

function Avatar({ src, name, village = false }: { src?: string; name: string; village?: boolean }) {
  if (village) {
    return <span className="search-pro-avatar search-pro-avatar--village"><HouseGlyph /></span>;
  }
  return (
    <span className="search-pro-avatar">
      {src ? <img src={src} alt="" /> : <span>{name.slice(0, 1)}</span>}
    </span>
  );
}

function highlightText(text: string, keyword: string) {
  if (!keyword.trim()) return text;
  const parts = text.split(new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return parts.map((part, index) =>
    part.toLowerCase() === keyword.toLowerCase() ? <mark key={index}>{part}</mark> : part
  );
}

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(() => searchParams.get('keyword') || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SearchResult>({ customers: [], villages: [], sales: [] });
  const [activeTab, setActiveTab] = useState<SearchTab>('all');
  const [searchError, setSearchError] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const historyRef = useRef<string[]>([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved) as string[];
      setHistory(parsed);
      historyRef.current = parsed;
    } catch {
      localStorage.removeItem(SEARCH_HISTORY_KEY);
    }
  }, []);

  const saveHistory = useCallback((value: string) => {
    const cleaned = value.trim();
    if (!cleaned) return;
    const next = [cleaned, ...historyRef.current.filter((item) => item !== cleaned)].slice(0, 8);
    historyRef.current = next;
    setHistory(next);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(next));
  }, []);

  const doSearch = useCallback(async (value: string, signal?: AbortSignal) => {
    if (!value.trim()) {
      setResult({ customers: [], villages: [], sales: [] });
      setSearched(false);
      setSearchError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    setSearchError(false);
    try {
      const response = await search(value.trim(), signal);
      if (response.code === 0) {
        setResult(response.data);
      }
    } catch (error: any) {
      if (error?.code !== 'ERR_CANCELED') {
        setResult({ customers: [], villages: [], sales: [] });
        setSearchError(true);
        Toast.show({ content: '搜索失败', icon: 'fail' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => doSearch(keyword, controller.signal), 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [keyword, doSearch]);

  const clearHistory = () => {
    historyRef.current = [];
    setHistory([]);
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  };

  const recordCount = result.sales.length;
  const totalCount = result.customers.length + result.villages.length + recordCount;
  const hasResult = totalCount > 0;
  const searchReturnPath = keyword.trim()
    ? `/search?keyword=${encodeURIComponent(keyword.trim())}`
    : '/search';
  const tabs: { key: SearchTab; label: string; count: number }[] = [
    { key: 'all', label: '全部', count: totalCount },
    { key: 'customers', label: '客户', count: result.customers.length },
    { key: 'villages', label: '村', count: result.villages.length },
    { key: 'records', label: '记录', count: recordCount }
  ];

  return (
    <main className="ios-page search-pro-page">
      <SafeArea className="search-pro-hero">
        <h1>搜索</h1>
        <p>客户、村与记录</p>
      </SafeArea>

      <label className="search-pro-field">
        <span className="search-pro-field__glyph"><SearchGlyph /></span>
        <input
          aria-label="搜索姓名、村或手机号"
          placeholder="搜索姓名、村或手机号"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') saveHistory(keyword);
          }}
        />
        {keyword && <button type="button" onClick={() => setKeyword('')}>清除</button>}
      </label>

      <nav className="search-pro-tabs" aria-label="搜索结果分类">
        {tabs.map((tab) => (
          <button
            type="button"
            key={tab.key}
            className={activeTab === tab.key ? 'is-active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </nav>

      <section className="search-pro-results" aria-live="polite">
        {!searched && history.length > 0 && (
          <motion.div className="search-pro-history" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <header><strong>最近搜索</strong><button type="button" onClick={clearHistory}>清除</button></header>
            <div>
              {history.map((item) => (
                <button type="button" key={item} onClick={() => setKeyword(item)}>
                  <ClockCircleOutline /> {item}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {loading && (
          <div className="search-pro-loading" aria-label="正在搜索">
            <span /><span /><span />
          </div>
        )}

        {!loading && !searched && history.length === 0 && (
          <EmptyState title="输入关键词开始搜索" description="支持搜索客户姓名、村名、电话" />
        )}

        {!loading && searched && searchError && (
          <EmptyState
            title="搜索失败"
            description="请检查网络后重试"
            action={(
              <button type="button" className="ios-button ios-button-primary" onClick={() => void doSearch(keyword)}>
                重新搜索
              </button>
            )}
          />
        )}

        {!loading && searched && !searchError && !hasResult && (
          <EmptyState title="未找到相关结果" description={`没有与「${keyword}」匹配的内容`} />
        )}

        {!loading && searched && !searchError && hasResult && (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.16 }}
            >
              {(activeTab === 'all' || activeTab === 'customers') && result.customers.length > 0 && (
                <section className="search-pro-section">
                  <h2>客户</h2>
                  {result.customers.map((customer: any) => (
                    <button type="button" className="search-pro-row" key={customer.id} onClick={() => {
                      saveHistory(keyword);
                      navigate(`/customer/${customer.id}?from=search&returnTo=${encodeURIComponent(searchReturnPath)}`);
                    }}>
                      <Avatar src={customer.avatar} name={customer.name} />
                      <span className="search-pro-row__body">
                        <strong>{highlightText(customer.name, keyword)}</strong>
                        <small className="search-pro-row__contact">
                          <span>{customer.village || '未填写村'}</span>
                          <span className="search-pro-row__phone">{customer.phone || '未填写电话'}</span>
                        </small>
                      </span>
                      <Chevron />
                    </button>
                  ))}
                </section>
              )}

              {(activeTab === 'all' || activeTab === 'villages') && result.villages.length > 0 && (
                <section className="search-pro-section">
                  <h2>村</h2>
                  {result.villages.map((village: any) => (
                    <button type="button" className="search-pro-row" key={village.village} onClick={() => {
                      saveHistory(keyword);
                      navigate(`/customers?village=${encodeURIComponent(village.village)}&from=search&returnTo=${encodeURIComponent(searchReturnPath)}`);
                    }}>
                      <Avatar village name={village.village} />
                      <span className="search-pro-row__body">
                        <strong>{highlightText(village.village, keyword)}</strong>
                        <small>{village.customer_count} 人 · 累计 {village.total_weight} 斤</small>
                      </span>
                      <span className="search-pro-row__action">查看</span>
                      <Chevron />
                    </button>
                  ))}
                </section>
              )}

              {(activeTab === 'all' || activeTab === 'records') && result.sales.length > 0 && (
                <section className="search-pro-section">
                  <h2>记录</h2>
                  {result.sales.map((record: any) => (
                    <button type="button" className="search-pro-row" key={`sale-${record.id}`} onClick={() => {
                      saveHistory(keyword);
                      navigate(`/sale/${record.id}/edit?from=search&returnTo=${encodeURIComponent(searchReturnPath)}`);
                    }}>
                      <Avatar src={record.customer_avatar} name={record.customer_name} />
                      <span className="search-pro-row__body">
                        <strong>{record.customer_name}</strong>
                        <small>{record.category_name} · {dayjs(record.sale_time).format('MM-DD HH:mm')}</small>
                      </span>
                      <span className="search-pro-row__value"><strong>¥{record.total_amount}</strong><small>{record.weight}{record.unit || '斤'}</small></span>
                      <Chevron />
                    </button>
                  ))}
                </section>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </section>
    </main>
  );
}
